import React, { useState } from 'react';
import { Target, Plus, Calendar, TrendingUp, CheckCircle2, Clock, AlertCircle, Edit } from 'lucide-react';
import Button from '../../../components/ui/Button';
import EmptyState from '../../../components/EmptyState';

const GoalManagement = ({ employee, goals, employees, onGoalCreate }) => {
  const [activeFilter, setActiveFilter] = useState('all');

  const getGoalStatusColor = (progress) => {
    if (progress >= 100) return 'text-green-600 bg-green-100 border-green-200';
    if (progress >= 75) return 'text-blue-600 bg-blue-100 border-blue-200';
    if (progress >= 50) return 'text-yellow-600 bg-yellow-100 border-yellow-200';
    return 'text-red-600 bg-red-100 border-red-200';
  };

  const getGoalStatusIcon = (progress, dueDate) => {
    const now = new Date();
    const due = dueDate ? new Date(dueDate) : null;
    const isOverdue = due && due < now && progress < 100;

    if (progress >= 100) return CheckCircle2;
    if (isOverdue) return AlertCircle;
    return Clock;
  };

  const getGoalStatusText = (progress, dueDate) => {
    const now = new Date();
    const due = dueDate ? new Date(dueDate) : null;
    const isOverdue = due && due < now && progress < 100;

    if (progress >= 100) return 'Completed';
    if (isOverdue) return 'Overdue';
    if (progress >= 75) return 'On Track';
    if (progress >= 50) return 'At Risk';
    return 'Behind';
  };

  // Filter goals based on active filter
  const filteredGoals = goals?.filter(goal => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'completed') return goal?.progress >= 100;
    if (activeFilter === 'in_progress') return goal?.progress > 0 && goal?.progress < 100;
    if (activeFilter === 'not_started') return goal?.progress === 0;
    if (activeFilter === 'overdue') {
      const now = new Date();
      const due = goal?.dueDate ? new Date(goal?.dueDate) : null;
      return due && due < now && goal?.progress < 100;
    }
    return true;
  }) || [];

  // Goal statistics
  const goalStats = {
    total: goals?.length || 0,
    completed: goals?.filter(g => g?.progress >= 100)?.length || 0,
    inProgress: goals?.filter(g => g?.progress > 0 && g?.progress < 100)?.length || 0,
    notStarted: goals?.filter(g => g?.progress === 0)?.length || 0,
    overdue: goals?.filter(g => {
      const now = new Date();
      const due = g?.dueDate ? new Date(g?.dueDate) : null;
      return due && due < now && g?.progress < 100;
    })?.length || 0
  };

  const filters = [
    { id: 'all', label: 'All Goals', count: goalStats?.total },
    { id: 'in_progress', label: 'In Progress', count: goalStats?.inProgress },
    { id: 'completed', label: 'Completed', count: goalStats?.completed },
    { id: 'overdue', label: 'Overdue', count: goalStats?.overdue }
  ];

  return (
    <div className="space-y-6">
      {/* Goal Management Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Target className="h-6 w-6 text-blue-600 mr-2" />
            Goal Management
            {employee && <span className="text-base font-normal text-gray-600 ml-2">- {employee?.firstName} {employee?.lastName}</span>}
          </h2>
          <Button onClick={onGoalCreate} className="flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Create Goal
          </Button>
        </div>

        {/* Goal Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{goalStats?.total}</div>
            <div className="text-sm text-blue-600">Total Goals</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{goalStats?.completed}</div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{goalStats?.inProgress}</div>
            <div className="text-sm text-yellow-600">In Progress</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{goalStats?.overdue}</div>
            <div className="text-sm text-red-600">Overdue</div>
          </div>
        </div>
      </div>
      {/* Goal Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap gap-2">
          {filters?.map((filter) => (
            <button
              key={filter?.id}
              onClick={() => setActiveFilter(filter?.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === filter?.id
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter?.label} ({filter?.count})
            </button>
          ))}
        </div>
      </div>
      {/* Goals List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {filteredGoals?.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredGoals?.map((goal, index) => {
              const StatusIcon = getGoalStatusIcon(goal?.progress, goal?.dueDate);
              const statusText = getGoalStatusText(goal?.progress, goal?.dueDate);
              const statusColor = getGoalStatusColor(goal?.progress);

              return (
                <div key={goal?.id || index} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <StatusIcon className={`h-5 w-5 ${statusColor?.split(' ')?.[0]}`} />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {goal?.title}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColor}`}>
                          {statusText}
                        </span>
                      </div>
                      
                      {goal?.description && (
                        <p className="text-gray-600 mb-3">{goal?.description}</p>
                      )}

                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        {goal?.dueDate && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>Due: {new Date(goal?.dueDate)?.toLocaleDateString()}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="h-4 w-4" />
                          <span>{Math.round(goal?.progress || 0)}% Complete</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Progress</span>
                      <span className="text-sm text-gray-500">{Math.round(goal?.progress || 0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          goal?.progress >= 100 ? 'bg-green-600' :
                          goal?.progress >= 75 ? 'bg-blue-600' :
                          goal?.progress >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                        }`}
                        style={{ width: `${Math.min(goal?.progress || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  {/* SMART Criteria Indicators */}
                  {goal?.smartCriteria && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {Object.entries(goal?.smartCriteria)?.map(([key, value]) => (
                        <span 
                          key={key}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {key?.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Action Buttons */}
                  <div className="mt-4 flex space-x-2">
                    <Button variant="outline" size="sm">
                      Update Progress
                    </Button>
                    <Button variant="ghost" size="sm">
                      Add Milestone
                    </Button>
                    <Button variant="ghost" size="sm">
                      View Details
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8">
            <EmptyState
              icon={Target}
              title={`No ${activeFilter !== 'all' ? activeFilter?.replace('_', ' ') : ''} goals found`}
              description={
                employee 
                  ? `${employee?.firstName} has no ${activeFilter !== 'all' ? activeFilter?.replace('_', ' ') : ''} goals set.`
                  : `No ${activeFilter !== 'all' ? activeFilter?.replace('_', ' ') : ''} goals found. Create goals to track progress and development.`
              }
              primaryAction={{
                label: 'Create Goal',
                icon: Plus,
                onClick: onGoalCreate
              }}
              secondaryAction={null}
            />
          </div>
        )}
      </div>
      {/* SMART Goals Guide */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">SMART Goals Framework</h3>
        <p className="text-blue-800 text-sm mb-4">
          Create effective goals using the SMART criteria for better tracking and achievement.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {[
            { letter: 'S', word: 'Specific', description: 'Clear and well-defined' },
            { letter: 'M', word: 'Measurable', description: 'Quantifiable progress' },
            { letter: 'A', word: 'Achievable', description: 'Realistic and attainable' },
            { letter: 'R', word: 'Relevant', description: 'Aligned with role/company' },
            { letter: 'T', word: 'Time-bound', description: 'Has a clear deadline' }
          ]?.map((item, index) => (
            <div key={index} className="bg-white rounded-lg p-3 text-center border border-blue-100">
              <div className="text-lg font-bold text-blue-600 mb-1">{item?.letter}</div>
              <div className="text-sm font-medium text-blue-900">{item?.word}</div>
              <div className="text-xs text-blue-700 mt-1">{item?.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GoalManagement;